import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { controlResetClassName, cx } from './classNames';

// MUI IconButton in the app theme: a bordered square on the surface colour,
// muted icon, secondary-hover fill, 6px radius, 2px focus outline, centred on
// the line when it sits inline in text (MUI's vertical-align: middle).
const BASE_CLASS_NAME = cx(
    controlResetClassName,
    'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-solid border-ui-line align-middle',
    'bg-ui-surface p-0 text-ui-muted hover:border-ui-hover-line hover:bg-ui-hover hover:text-ui-ink',
    'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
    'focus-visible:outline-ui-focus disabled:cursor-default disabled:border-ui-disabled-line',
    'disabled:bg-ui-disabled-fill disabled:text-ui-disabled-text',
);

// md is the app's control height (36px, 30px compact); sm matches the thin
// panel-header buttons (compactIconButtonSx); xs fits inside a table header.
const SIZE_CLASS_NAMES = Object.freeze({
    md: 'h-9 w-9 [[data-compact]_&]:h-[30px] [[data-compact]_&]:w-[30px]',
    sm: 'h-[26px] w-[26px]',
    xs: 'h-6 w-6',
});

/**
 * An icon-only button. `label` is its accessible name (wrap it in a Tooltip
 * to show the label on hover); `children` is the icon. Other props go to the
 * <button>.
 */
const IconButton = forwardRef(function IconButton({
    label,
    size = 'md',
    type = 'button',
    className,
    children,
    ...buttonProps
}, ref) {
    return (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            className={cx(BASE_CLASS_NAME, SIZE_CLASS_NAMES[size], className)}
            {...buttonProps}
        >
            {children}
        </button>
    );
});

IconButton.propTypes = {
    label: PropTypes.string.isRequired,
    size: PropTypes.oneOf(['md', 'sm', 'xs']),
    type: PropTypes.oneOf(['button', 'submit', 'reset']),
    className: PropTypes.string,
    children: PropTypes.node,
};

export default IconButton;
